import { InteractionStepType, UIGenerationEnforcementMode } from '@scale/shared/types/chat';
import StringInputBuilder from 'react-scale/corp/components/QM/taskTemplatesEditor/StringInputBuilder';
import BooleanInputBuilder from 'react-scale/corp/components/QM/taskTemplatesEditor/BooleanInputBuilder';
import { AdditionalConstraint } from '@scale/shared-types';
import { SelectMenu, Switch, Typography } from '@scale/scaleui';
import {
  ReviewLevelConfig,
  EnableRTLTextConfig,
  ConditionalStepConfig,
} from 'react-scale/components/scalers/tasks/ThreadedInteraction/definitions/configs';
import OverrideIDBuilder from 'react-scale/corp/components/QM/taskTemplatesEditor/OverrideIDBuilder';
import ResponseConstraintsBuilder from 'react-scale/corp/components/QM/pipeline/ResponseConstraintsBuilder';
import { typeBuilderMap } from 'react-scale/corp/components/QM/taskTemplatesEditor/BuilderSchemas';
import DefaultTextCollectionFieldIdsBuilder from 'react-scale/corp/components/QM/pipeline/DefaultTextCollectionFieldIdsBuilder';
import produce from 'immer';
import {
  RESPONSE_CONSTRAINTS_HINT,
  ADDITIONAL_REFERENCE_TEXT_HINT,
} from 'react-scale/lib/ml_constants';
import { StepConfigurationComponentProps } from '../stepDescriptor/types';
import { strategyRegistry } from 'react-scale/components/scalers/tasks/ThreadedInteraction/definitions/ModelResponseEditor/NativeToolCallEditor/ToolCallStrategies/StrategyRegistry';
import { ZodFormBuilder } from 'react-scale/corp/views/CorpProjectGroupManager/views/Linter/GenericLinterView/ZodFormBuilder';
import { ExperimentalFeature } from 'react-scale/components/scalers/tasks/ThreadedInteraction/ChatConfigurationEditor/components/ConfigLifeCycleInfo';
import { z } from 'zod';
import { Select } from '@scale/scaleui-radix';

export function ModelResponseEditorConfigurationComponent({
  params: currentParams,
  previousParams,
  setParams: setCurrentParams,
  taskParams,
  displayMLQualitySettings,
}: StepConfigurationComponentProps<InteractionStepType.ModelResponseEditor>) {
  const StrategyNames = Object.keys(strategyRegistry);
  const currentStrategy = currentParams.params.tool_call_strategy || '';
  const currentStrategyParamSchema = strategyRegistry[currentStrategy]?.paramSchema || z.object({});

  return (
    <div className="flex flex-col gap-4">
      <StringInputBuilder
        name={'Title'}
        existingValue={currentParams.title}
        updateProperty={(value: string) => setCurrentParams({ ...currentParams, title: value })}
        required={true}
      />

      <OverrideIDBuilder
        params={currentParams}
        setParams={setCurrentParams}
        previousParams={previousParams}
      />

      {displayMLQualitySettings && (
        <>
          <DefaultTextCollectionFieldIdsBuilder
            name={'Additional Reference Text'}
            existingValue={currentParams.params.additional_reference_text}
            updateProperty={(value: string[] | string) =>
              setCurrentParams(
                produce(currentParams, draft => {
                  draft.params.additional_reference_text = Array.isArray(value) ? value : [value];
                  return draft;
                }),
              )
            }
            required={false}
            hint={ADDITIONAL_REFERENCE_TEXT_HINT}
            allowMultiple={true}
            taskTemplateParams={taskParams}
          />

          <ResponseConstraintsBuilder
            typeBuilderMap={typeBuilderMap}
            name={'Response Constraints'}
            existingValue={currentParams.params.response_constraints}
            updateProperty={(value: AdditionalConstraint[] | undefined) =>
              setCurrentParams(
                produce(currentParams, draft => {
                  draft.params.response_constraints = value;
                  return draft;
                }),
              )
            }
            required={false}
            hint={RESPONSE_CONSTRAINTS_HINT}
            taskTemplateParams={taskParams}
          />
        </>
      )}

      <StringInputBuilder
        name={'Subtitle'}
        existingValue={currentParams.subtitle}
        updateProperty={(value: string) => setCurrentParams({ ...currentParams, subtitle: value })}
      />

      <StringInputBuilder
        name={'Regex Condition for Editor Termination'}
        existingValue={currentParams.params.termination_regex_condition}
        updateProperty={(value: string) =>
          setCurrentParams(
            produce(currentParams, draft => {
              draft.params.termination_regex_condition = value;
              return draft;
            }),
          )
        }
      />

      <Switch
        altDescription="Use Native Tool Call Editor"
        label="Use Native Tool Call Editor"
        checked={!!currentParams.params.use_tool_call_editor}
        labelDescription="Use an editor tailored for editing native tool calls. Please verify with Engineering to see if this is available for your use case."
        onCheckedChange={checked =>
          setCurrentParams(
            produce(currentParams, draft => {
              // Ensure the tool call strategy and params are reset when the switch is toggled
              draft.params.use_tool_call_editor = checked;
              if (checked) {
                // Initialize the tool call strategy and params to empty values
                draft.params.tool_call_strategy = '';
                draft.params.tool_call_strategy_params = {};
              } else {
                // Reset to null when the switch is toggled off
                draft.params.tool_call_strategy = undefined;
                draft.params.tool_call_strategy_params = undefined;
              }
              return draft;
            }),
          )
        }
      />
      {currentParams.params.use_tool_call_editor && (
        <>
          <SelectMenu
            label={`Tool Call Strategy`}
            options={StrategyNames.map(strategy => ({
              id: strategy,
              name: strategy,
            }))}
            selectedOptionId={currentStrategy}
            setSelected={tool_call_strategy =>
              setCurrentParams(
                produce(currentParams, draft => {
                  draft.params.tool_call_strategy = tool_call_strategy;
                  return draft;
                }),
              )
            }
            required={true}
            error={!StrategyNames.includes(currentStrategy)}
          />
          {/* Create a form off of the zod type currentStrategyParamSchema */}
          {Object.keys(currentStrategyParamSchema.shape).length > 0 && (
            <ZodFormBuilder
              schema={currentStrategyParamSchema}
              onChange={(values: any) =>
                setCurrentParams(
                  produce(currentParams, draft => {
                    draft.params.tool_call_strategy_params = values;
                    return draft;
                  }),
                )
              }
              label={`Tool Strategy Params`}
              value={currentParams.params.tool_call_strategy_params}
              startOpen={true}
            />
          )}
        </>
      )}

      {!currentParams.params.use_tool_call_editor && (
        <>
          <div className="flex flex-col gap-2">
            <BooleanInputBuilder
              name={'Enable Inline Comments'}
              existingValue={!!currentParams.params.enable_inline_comments}
              updateProperty={(value: boolean) =>
                setCurrentParams(
                  produce(currentParams, draft => {
                    draft.params.enable_inline_comments = value;
                    return draft;
                  }),
                )
              }
            />
            <div className="prose text-sm italic ml-8">
              If checked, tasker will be able to highlight and leave comments on the response shown.
              The feedback is then shown to the last writer who attempted it if the task is SBQed.
            </div>
          </div>
          <EnableRTLTextConfig
            value={currentParams.params.enable_rtl_text}
            onChange={value => {
              setCurrentParams(
                produce(currentParams, draft => {
                  draft.params.enable_rtl_text = value;
                  return draft;
                }),
              );
            }}
          />
        </>
      )}

      <ExperimentalFeature
        name="Enable UI generation"
        details="If enabled, allows contributors to render a UI of the code"
        showInfoCondition={!!currentParams.params.enable_ui_generation}
      >
        <div className="flex flex-col gap-2">
          <BooleanInputBuilder
            name="Enable UI generation"
            existingValue={!!currentParams.params.enable_ui_generation}
            updateProperty={(value: boolean) =>
              setCurrentParams({
                ...currentParams,
                params: { ...currentParams.params, enable_ui_generation: value },
              })
            }
          />
          <div className="prose text-sm italic ml-8">
            If enabled, allows contributors to render a UI of the code
          </div>
        </div>
      </ExperimentalFeature>

      {!!currentParams.params.enable_ui_generation && (
        <div className="flex flex-col gap-2">
          <Typography.Subtitle2>UI generation enforcement mode</Typography.Subtitle2>
          <Select.Root
            size="2"
            value={
              currentParams.params.ui_generation_enforcement_mode ??
              UIGenerationEnforcementMode.None
            }
            onValueChange={(value: string) =>
              setCurrentParams({
                ...currentParams,
                params: {
                  ...currentParams.params,
                  ui_generation_enforcement_mode: value as UIGenerationEnforcementMode,
                },
              })
            }
          >
            <Select.Trigger placeholder="Select UI generation enforcement mode" />
            <Select.Content position="popper">
              <Select.Item value={UIGenerationEnforcementMode.None}>None</Select.Item>
              <Select.Item value={UIGenerationEnforcementMode.CompilationAttempted}>
                Must Attempt Compilation
              </Select.Item>
              <Select.Item value={UIGenerationEnforcementMode.CompilationSuccessful}>
                Must Compile Successfully
              </Select.Item>
            </Select.Content>
          </Select.Root>
        </div>
      )}

      <ReviewLevelConfig currentParams={currentParams} setCurrentParams={setCurrentParams} />
      <ConditionalStepConfig
        currentParams={currentParams}
        setCurrentParams={setCurrentParams}
        previousParams={previousParams}
        taskParams={taskParams}
      />
    </div>
  );
}
